/**
 * The Werks — Garage.
 *
 * A visitor picks their chassis once. It persists, follows them across the
 * site, and turns every product page into a yes/no fitment answer instead of a
 * spec table they have to interpret.
 *
 * Storage shape: { code, make, model, url }
 * Broadcasts `werks:garage` on every change so any component can react.
 */

const KEY = 'werks:garage';
const EVENT = 'werks:garage';

export const Garage = {
  get() {
    try {
      const raw = window.localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  },

  set(vehicle) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(vehicle));
    } catch (error) {
      /* private mode — session-only is an acceptable degradation */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: vehicle }));
  },

  clear() {
    try {
      window.localStorage.removeItem(KEY);
    } catch (error) {
      /* nothing to do */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
  },
};

/** Normalises "S13", "s13 ", "chassis_S13" to a comparable token. */
function token(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^chassis[_-]/, '');
}

/**
 * Decides whether a product fits the garaged car.
 * Returns 'fits' | 'universal' | 'no' | 'unknown'.
 */
export function verdictFor(fitmentList, vehicle) {
  const list = (fitmentList || '')
    .split(',')
    .map(token)
    .filter(Boolean);

  if (list.includes('universal')) return 'universal';
  if (!vehicle) return 'unknown';
  if (list.length === 0) return 'unknown';
  return list.includes(token(vehicle.code)) ? 'fits' : 'no';
}

/* ------------------------------------------------------------------ chip */

class WerksGarageChip extends HTMLElement {
  connectedCallback() {
    this.render();
    this.onChange = () => this.render();
    document.addEventListener(EVENT, this.onChange);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-garage-clear]')) {
        event.preventDefault();
        Garage.clear();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onChange);
  }

  render() {
    const vehicle = Garage.get();
    const href = this.dataset.pickerUrl || '/';

    if (!vehicle) {
      this.innerHTML = `
        <a class="werks-garage-chip werks-garage-chip--empty" href="${href}">
          <span class="werks-garage-chip__plus" aria-hidden="true">+</span>
          <span>${this.dataset.emptyLabel || 'Add your car'}</span>
        </a>`;
      return;
    }

    this.innerHTML = `
      <span class="werks-garage-chip">
        <a class="werks-garage-chip__link" href="${vehicle.url || href}">
          <span class="werks-garage-chip__label">Garage</span>
          <span class="werks-garage-chip__code">${vehicle.code}</span>
        </a>
        <button type="button" class="werks-garage-chip__clear" data-garage-clear aria-label="Clear ${vehicle.code} from garage">&times;</button>
      </span>`;
  }
}

if (!customElements.get('werks-garage-chip')) {
  customElements.define('werks-garage-chip', WerksGarageChip);
}

/* ------------------------------------------------------- fitment verdict */

class WerksFitment extends HTMLElement {
  connectedCallback() {
    this.render();
    this.onChange = () => this.render();
    document.addEventListener(EVENT, this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onChange);
  }

  render() {
    const vehicle = Garage.get();
    const verdict = verdictFor(this.dataset.fitment, vehicle);
    const pickerUrl = this.dataset.pickerUrl || '/';

    this.dataset.verdict = verdict;

    const copy = {
      fits: {
        icon: '&check;',
        title: `Fits your ${vehicle?.code || ''}`.trim(),
        body: this.dataset.fitsNote || 'Confirmed for this chassis.',
      },
      universal: {
        icon: '&infin;',
        title: 'Fits any chassis',
        body: 'Universal fitment — no vehicle-specific parts needed.',
      },
      no: {
        icon: '!',
        title: `Not listed for your ${vehicle?.code || 'car'}`,
        body: 'This may still work with modification. Ask us before you order.',
      },
      unknown: {
        icon: '?',
        title: 'Check fitment',
        body: 'Add your chassis and we will confirm this part against it.',
      },
    }[verdict];

    const action =
      verdict === 'unknown'
        ? `<a class="werks-fitment__action" href="${pickerUrl}">Add your car</a>`
        : verdict === 'no'
          ? `<a class="werks-fitment__action" href="${this.dataset.contactUrl || '/pages/contact'}">Ask about fitment</a>`
          : '';

    this.innerHTML = `
      <div class="werks-fitment__row">
        <span class="werks-fitment__icon" aria-hidden="true">${copy.icon}</span>
        <span class="werks-fitment__body">
          <strong>${copy.title}</strong>
          <span>${copy.body}</span>
        </span>
        ${action}
      </div>`;
  }
}

if (!customElements.get('werks-fitment')) {
  customElements.define('werks-fitment', WerksFitment);
}

/* --------------------------------------------------------- garage picker */

class WerksGaragePicker extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-garage-card]')];
    this.makeSelect = this.querySelector('[data-garage-make]');
    this.codeSelect = this.querySelector('[data-garage-code]');
    this.submit = this.querySelector('[data-garage-submit]');
    this.status = this.querySelector('[data-garage-status]');

    this.rows = this.cards.map((card) => ({ ...card.dataset }));
    this.yearInput = this.querySelector('[data-garage-year]');
    this.hint = this.querySelector('[data-garage-hint]');

    this.cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        // Modifier-clicks and middle-clicks should behave like normal links.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        this.choose(card.dataset, true);
      });
    });

    if (this.makeSelect) this.initSelects();

    this.onChange = () => this.reflect();
    document.addEventListener(EVENT, this.onChange);
    this.reflect();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onChange);
  }

  initSelects() {
    this.populateMakes();

    // A year is what most people know about their car; the chassis code is
    // what the catalogue is organised by. Typing a year narrows both lists to
    // whatever was actually built that year instead of adding a third filter.
    this.yearInput?.addEventListener('input', () => {
      this.populateMakes();
      this.makeSelect.dispatchEvent(new Event('change'));
      this.reportYear();
    });

    this.makeSelect.addEventListener('change', () => {
      this.codeSelect.innerHTML = '<option value="">Select chassis</option>';
      this.matching()
        .filter((r) => r.make === this.makeSelect.value)
        .forEach((r) => {
          const option = document.createElement('option');
          option.value = r.code;
          option.textContent = r.model ? `${r.code} — ${r.model}` : r.code;
          this.codeSelect.append(option);
        });
      this.codeSelect.disabled = this.codeSelect.options.length < 2;
      this.submit.disabled = true;
    });

    this.codeSelect.addEventListener('change', () => {
      this.submit.disabled = !this.codeSelect.value;
    });

    this.submit.addEventListener('click', (event) => {
      event.preventDefault();
      const row = this.rows.find((r) => r.code === this.codeSelect.value);
      if (row) this.choose(row, true);
    });
  }

  /** Year typed in the box, or null when it isn't a plausible one. */
  year() {
    const value = Number(this.yearInput?.value);
    return Number.isInteger(value) && value >= 1960 && value <= 2035 ? value : null;
  }

  /** Chassis whose production range covers the typed year. */
  matching() {
    const year = this.year();
    if (!year) return this.rows;

    const hits = this.rows.filter((r) => {
      const from = Number(r.yearFrom);
      const to = Number(r.yearTo);
      if (!from && !to) return false;
      return year >= (from || -Infinity) && year <= (to || Infinity);
    });

    // A year nobody has ranges for shouldn't empty the picker.
    return hits.length ? hits : this.rows;
  }

  populateMakes() {
    const current = this.makeSelect.value;
    const makes = [...new Set(this.matching().map((r) => r.make))].filter(Boolean).sort();

    this.makeSelect.innerHTML = '<option value="">Select make</option>';
    makes.forEach((make) => {
      const option = document.createElement('option');
      option.value = make;
      option.textContent = make;
      this.makeSelect.append(option);
    });

    if (makes.includes(current)) this.makeSelect.value = current;
  }

  reportYear() {
    if (!this.hint) return;
    const year = this.year();

    if (!year) {
      this.hint.textContent = '';
      return;
    }

    const hits = this.rows.filter((r) => {
      const from = Number(r.yearFrom);
      const to = Number(r.yearTo);
      if (!from && !to) return false;
      return year >= (from || -Infinity) && year <= (to || Infinity);
    });

    if (hits.length === 0) {
      this.hint.textContent = `Nothing on file for ${year} — pick a chassis below.`;
      return;
    }

    const codes = hits.map((h) => h.code).join(', ');
    this.hint.textContent =
      hits.length === 1
        ? `${year} — that's the ${codes}.`
        : `${year} — narrowed to ${codes}.`;

    // Only one candidate: fill both selects so it's one click to save.
    if (hits.length === 1) {
      this.makeSelect.value = hits[0].make;
      this.makeSelect.dispatchEvent(new Event('change'));
      this.codeSelect.value = hits[0].code;
      this.submit.disabled = false;
    }
  }

  choose(row, navigate) {
    Garage.set({ code: row.code, make: row.make, model: row.model, url: row.url });
    if (navigate && row.url && this.dataset.navigate !== 'false') {
      window.location.href = row.url;
    }
  }

  reflect() {
    const vehicle = Garage.get();
    this.cards.forEach((card) => {
      card.toggleAttribute('data-active', Boolean(vehicle) && card.dataset.code === vehicle.code);
    });

    if (!this.status) return;
    this.status.innerHTML = vehicle
      ? `In your garage: <strong>${vehicle.code}</strong> ${vehicle.model || ''} &middot; <button type="button" data-garage-reset>change</button>`
      : '';

    this.status.querySelector('[data-garage-reset]')?.addEventListener('click', () => Garage.clear());
  }
}

if (!customElements.get('werks-garage-picker')) {
  customElements.define('werks-garage-picker', WerksGaragePicker);
}
